import type { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios, { AxiosError } from 'axios';
import type { ServiceError, ServiceResult, ServiceSuccess } from '@core/models/actionResponse';
import { isServiceSuccess } from '@core/models/actionResponse';
import { authenticationHandler } from './authenticationHandler';
import { router } from '../routing';
import { identifier } from '@core/models/base';
import { ImageLookupDisplayModel } from './models/images/imageLookUps';
import { SubjectCreateModel, SubjectDisplayModel } from './models/subjects/subjects';
import { WorkflowDisplayModel, WorkflowCreateModel, WorkflowUpdateModel } from './models/workflows/workflows';

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error instanceof AxiosError && error.response?.status === 503) {
      router.push('/maintenance');
    }

    return Promise.reject(error);
  },
);

export const authedAxios = axios.create({});

authedAxios.interceptors.request.use(
  async (config) => {
    const token = await authenticationHandler.getToken();
    if (config && isServiceSuccess(token)) {
      config.headers['Authorization'] = `Bearer ${token.data.token}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

export const getAuthorizationHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? `Bearer ${token}` : null;
};

export const uploadFile = async <Tout>(
  url: string,
  file: File,
  method: 'PUT' | 'POST' | 'PATCH',
  fileFormName = 'File',
) => {
  const data = new FormData();
  data.append(fileFormName, file);
  return authedRequest<Tout>({
    method,
    url,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadForm = async <Tout>(url: string, data: FormData, method: 'PUT' | 'POST' | 'PATCH') => {
  return authedRequest<Tout>({
    method,
    url,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const get = async <Tout>(url: string, params?: any) => {
  return await authedRequest<Tout>({
    url,
    params,
    method: 'get',
  });
};

export const post = async <Tout>(url: string, data: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'post',
  });
};

export const patch = async <Tout>(url: string, data?: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'patch',
  });
};

export const put = async <Tout>(url: string, data?: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'put',
  });
};

export const deleteReq = async <Tout>(url: string) => {
  return await authedRequest<Tout>({ url, method: 'delete' });
};

export const axiosToServiceResult = <Tout>(axiosReq: AxiosPromise<Tout>): Promise<ServiceResult<Tout>> => {
  return new Promise((resolve) => {
    axiosReq
      .then((response) => {
        const thing = (response as AxiosResponse<Tout>).data;
        resolve({ data: thing } as ServiceSuccess<Tout>);
      })
      .catch((error) => {
        console.log('Request error', error);
        resolve({ errors: error?.response?.data || [error] } as ServiceError);
      });
  });
};

export const authedRequest = <Tout>(config: AxiosRequestConfig<any>): Promise<ServiceResult<Tout>> => {
  return axiosToServiceResult(authedAxios(config));
};

export const apiClient = {
  v1: {
    imageLookups: {
      replaceImage: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
        file: File,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        const formData = new FormData();
        formData.append('file', file);

        return uploadForm(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}`,
          formData,
          'PATCH',
        );
      },

      deleteImage: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<identifier>> => {
        return deleteReq(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}`,
        );
      },

      convertImageType: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        return patch(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}/convert-type`,
        );
      },

      generateThumbnail: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        return post(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}/generate-thumbnail`,
          {},
        );
      },
    },
  },

  v3: {
    subjects: {
      async create(
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        createModel: SubjectCreateModel,
      ) {
        return await post<SubjectDisplayModel>(
          `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects`,
          createModel,
        );
      },

      async createBulk(
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        createModels: SubjectCreateModel[],
        returnData?: boolean,
      ) {
        const url = returnData
          ? `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects/bulk?returnData=true`
          : `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects/bulk`;

        return await post<SubjectDisplayModel[]>(url, createModels);
      },
    },

    workflows: {
      async getWorkflows() {
        const result = await get<WorkflowDisplayModel[]>('/api/v3/workflows');
        if (isServiceSuccess(result)) {
          return result.data;
        } else {
          throw new Error('Failed to fetch workflows');
        }
      },
    },
  },
};

export const adminApiClient = {
  workflows: {
    async getWorkflows() {
      const result = await get<WorkflowDisplayModel[]>('/api/admin/workflows');
      if (isServiceSuccess(result)) {
        return result.data;
      } else {
        throw new Error('Failed to fetch workflows');
      }
    },

    async createWorkflow(workflow: WorkflowCreateModel) {
      return await post<WorkflowDisplayModel>('/api/admin/workflows', workflow);
    },

    async updateWorkflow(workflow: WorkflowUpdateModel) {
      return await put<WorkflowDisplayModel>(`/api/admin/workflows/${workflow.id}`, workflow);
    },

    async deleteWorkflow(id: number) {
      const result = await deleteReq<void>(`/api/admin/workflows/${id}`);
      if (!isServiceSuccess(result)) {
        throw new Error(`Failed to delete workflow with ID ${id}`);
      }
    },
  },
};
